import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Title,
  Button,
  Text,
  Group,
  Stack,
  Progress,
  Card,
  Loader,
  Alert,
  ActionIcon,
  Box,
  useMantineColorScheme,
} from '@mantine/core';
import {
  IconArrowLeft,
  IconEye,
  IconCheck,
  IconArrowRight,
  IconInfoCircle,
  IconCards,
} from '@tabler/icons-react';
import { flashcardService } from '../services/flashcardService';
import { FlashcardDTO, FlashcardFilters, FlashcardLoadingState } from '../Models/Flashcard';
import QuejaSugerenciaButton from '../components/QuejaSugerenciaButton';

const EstudiarFlashcardsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { colorScheme } = useMantineColorScheme();
  const dark = colorScheme === 'dark';

  // ---- Design tokens (mismo lenguaje que el resto del sistema) ----
  const cardStyle: React.CSSProperties = {
    backgroundColor: dark ? 'rgba(30, 41, 59, 0.7)' : 'rgba(247, 243, 238, 0.95)',
    border: `1px solid ${dark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(221, 216, 209, 0.8)'}`,
    boxShadow: 'none',
  };
  const textColor = dark ? '#e2e8f0' : '#2d2a26';
  const mutedColor = dark ? '#94a3b8' : '#8c8478';
  const primaryButtonStyles = {
    root: {
      backgroundColor: dark ? '#e2e8f0' : '#1f2937',
      color: dark ? '#1f2937' : '#ffffff',
      '&:hover': { backgroundColor: dark ? '#cbd5e1' : '#111827' },
    },
  };
  const secondaryButtonStyles = {
    root: {
      borderColor: dark ? 'rgba(255,255,255,0.18)' : 'rgba(221, 216, 209, 0.8)',
      color: textColor,
      backgroundColor: 'transparent',
      '&:hover': {
        backgroundColor: dark ? 'rgba(255,255,255,0.04)' : 'rgba(45,42,38,0.04)',
      },
    },
  };

  // Estados principales
  const [flashcards, setFlashcards] = useState<FlashcardDTO[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [loadingState, setLoadingState] = useState<FlashcardLoadingState>('loading');
  const [error, setError] = useState<string | null>(null);
  const [flashcardsEstudiadas, setFlashcardsEstudiadas] = useState(0);

  // Parámetros URL
  const usuarioIdParam = parseInt(searchParams.get('usuarioId') || '0');
  const idsParam = searchParams.get('ids');
  const modoParam = (searchParams.get('modo') as 'NUEVAS' | 'REPASO' | 'MEZCLA' | null) || null;
  const limitParam = searchParams.get('limit') ? parseInt(searchParams.get('limit')!) : undefined;
  const shuffleParam = searchParams.get('shuffle') === 'true';

  const filtros: FlashcardFilters = {
    especialidadId: searchParams.get('especialidadId') ? parseInt(searchParams.get('especialidadId')!) : undefined,
    temaId: searchParams.get('temaId') ? parseInt(searchParams.get('temaId')!) : undefined,
    soloNoEstudiadas: searchParams.get('soloNoEstudiadas') === 'true',
    usuarioId: usuarioIdParam,
  };

  useEffect(() => {
    // Cancellation flag: React StrictMode (dev) monta dos veces. Sin esto, la
    // primera respuesta llega, se pinta una card, y luego la segunda la
    // sobrescribe — el usuario ve un "salto" entre cards al inicio.
    let cancelled = false;

    const loadFlashcards = async () => {
      try {
        setLoadingState('loading');
        let data;
        if (idsParam) {
          const ids = idsParam.split(',').map((s) => parseInt(s.trim())).filter((n) => !isNaN(n));
          data = await flashcardService.obtenerPorIds(ids, usuarioIdParam || undefined);
          if (shuffleParam && data.length > 1) {
            data = [...data].sort(() => Math.random() - 0.5);
          }
        } else {
          data = await flashcardService.obtenerFlashcards({
            ...filtros,
            modo: modoParam ?? undefined,
            limit: limitParam,
            shuffle: shuffleParam,
          });
        }

        if (cancelled) return;

        if (data.length === 0) {
          setLoadingState('empty');
        } else {
          setFlashcards(data);
          setCurrentIndex(0);
          setShowAnswer(false);
          setLoadingState('estudiando');
        }
      } catch (e) {
        if (cancelled) return;
        console.error('Error al cargar flashcards:', e);
        setError('Error al cargar las flashcards. Por favor, intenta de nuevo.');
        setLoadingState('error');
      }
    };

    if (usuarioIdParam > 0) {
      loadFlashcards();
    } else {
      setError('Error: Parámetros de usuario inválidos');
      setLoadingState('error');
    }

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentFlashcard = flashcards[currentIndex];
  const totalFlashcards = flashcards.length;
  const progreso = totalFlashcards > 0 ? ((currentIndex + 1) / totalFlashcards) * 100 : 0;

  const handleShowAnswer = () => setShowAnswer(true);

  const handleMarkAsStudied = async () => {
    if (!currentFlashcard) return;
    try {
      await flashcardService.marcarComoEstudiada(currentFlashcard.id, filtros.usuarioId);
      setFlashcardsEstudiadas((prev) => prev + 1);
      setFlashcards((prev) =>
        prev.map((fc, idx) =>
          idx === currentIndex
            ? { ...fc, estudiado: true, vecesEstudiado: fc.vecesEstudiado + 1 }
            : fc
        )
      );
      handleNextFlashcard();
    } catch (e) {
      console.error('Error al marcar como estudiada:', e);
    }
  };

  const handleNextFlashcard = () => {
    if (currentIndex < totalFlashcards - 1) {
      setCurrentIndex((prev) => prev + 1);
      setShowAnswer(false);
    } else {
      navigateToSummary();
    }
  };

  const navigateToSummary = () => {
    const summaryParams = new URLSearchParams();
    summaryParams.append('totalFlashcards', totalFlashcards.toString());
    summaryParams.append('flashcardsEstudiadas', flashcardsEstudiadas.toString());
    summaryParams.append('usuarioId', filtros.usuarioId.toString());
    if (filtros.especialidadId) {
      summaryParams.append('especialidadId', filtros.especialidadId.toString());
    }
    navigate(`/estudiante/flashcards/resumen?${summaryParams.toString()}`);
  };

  const handleExit = () => navigate('/estudiante/flashcards');

  const handleBackWithConfirm = () => {
    const ok = window.confirm(
      '¿Seguro que quieres salir? Se perderá el progreso de esta sesión de estudio.'
    );
    if (ok) handleExit();
  };

  // ---------------- Estados de carga ----------------
  if (loadingState === 'loading') {
    return (
      <Box style={pageBackground(dark)}>
        <Group justify="center" pt="xl">
          <Loader size="lg" />
          <Text style={{ color: mutedColor }}>Cargando tarjetas...</Text>
        </Group>
      </Box>
    );
  }

  if (loadingState === 'error' || error) {
    return (
      <Box style={pageBackground(dark)}>
        <Box style={{ maxWidth: 640, margin: '0 auto', padding: '24px' }}>
          <Alert icon={<IconInfoCircle size={16} />} title="Error" color="red" variant="light">
            {error}
          </Alert>
        </Box>
      </Box>
    );
  }

  if (loadingState === 'empty') {
    return (
      <Box style={pageBackground(dark)}>
        <Box style={{ maxWidth: 640, margin: '0 auto', padding: '40px 24px' }}>
          <Card radius="lg" p="xl" ta="center" style={cardStyle}>
            <IconCards size={48} color={mutedColor} style={{ margin: '0 auto 12px' }} />
            <Title
              order={4}
              mb="xs"
              style={{
                fontFamily: 'Space Grotesk, Inter, sans-serif',
                color: textColor,
                fontWeight: 600,
              }}
            >
              No hay tarjetas disponibles
            </Title>
            <Text size="sm" mb="lg" style={{ color: mutedColor }}>
              No se encontraron tarjetas con los filtros seleccionados.
              Intenta cambiar los criterios de búsqueda.
            </Text>
            <Button onClick={handleExit} radius="md" styles={primaryButtonStyles}>
              Volver
            </Button>
          </Card>
        </Box>
      </Box>
    );
  }

  // ---------------- Estudiando ----------------
  return (
    <Box style={pageBackground(dark)}>
      <Box style={{ maxWidth: 760, margin: '0 auto', padding: '24px 24px 40px' }}>
        <Stack gap="lg">
          {/* Header con progreso */}
          <Group align="center" wrap="nowrap">
            <ActionIcon
              variant="default"
              size="lg"
              radius="md"
              onClick={handleBackWithConfirm}
              aria-label="Salir"
              styles={{
                root: {
                  borderColor: dark ? 'rgba(255,255,255,0.12)' : 'rgba(221, 216, 209, 0.8)',
                  backgroundColor: 'transparent',
                  color: textColor,
                },
              }}
            >
              <IconArrowLeft size={18} />
            </ActionIcon>

            <Box style={{ flex: 1 }}>
              <Text
                size="xs"
                ta="center"
                mb={6}
                style={{ color: mutedColor, fontFamily: 'Inter, sans-serif' }}
              >
                Tarjeta {currentIndex + 1} de {totalFlashcards}
              </Text>
              <Progress
                value={progreso}
                size="sm"
                radius="xl"
                color={dark ? 'gray.4' : 'dark.6'}
              />
            </Box>
          </Group>

          {/* Tarjeta con flip */}
          <Box style={{ perspective: '1000px', height: '420px' }}>
            <Box
              style={{
                position: 'relative',
                width: '100%',
                height: '100%',
                transformStyle: 'preserve-3d',
                transition: 'transform 0.6s',
                transform: showAnswer ? 'rotateY(180deg)' : 'rotateY(0deg)',
                cursor: !showAnswer ? 'pointer' : 'default',
              }}
              onClick={!showAnswer ? handleShowAnswer : undefined}
            >
              {/* Frente */}
              <Card
                radius="lg"
                p="xl"
                style={{
                  ...cardStyle,
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  backfaceVisibility: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                {currentFlashcard?.id && (
                  <Box
                    style={{ position: 'absolute', top: 12, right: 12, zIndex: 2 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <QuejaSugerenciaButton
                      tipoObjetivo="FLASHCARD"
                      flashcardId={currentFlashcard.id}
                    />
                  </Box>
                )}

                <Text
                  size="xl"
                  fw={500}
                  ta="center"
                  mb="xl"
                  style={{ color: textColor, fontFamily: 'Inter, sans-serif', lineHeight: 1.5 }}
                >
                  {currentFlashcard?.pregunta}
                </Text>

                {!showAnswer && (
                  <Button
                    size="md"
                    radius="md"
                    leftSection={<IconEye size={16} />}
                    onClick={handleShowAnswer}
                    styles={primaryButtonStyles}
                  >
                    Ver respuesta
                  </Button>
                )}
              </Card>

              {/* Reverso */}
              <Card
                radius="lg"
                p="xl"
                style={{
                  ...cardStyle,
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  backfaceVisibility: 'hidden',
                  transform: 'rotateY(180deg)',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                {currentFlashcard?.id && (
                  <Box
                    style={{ position: 'absolute', top: 12, right: 12, zIndex: 2 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <QuejaSugerenciaButton
                      tipoObjetivo="FLASHCARD"
                      flashcardId={currentFlashcard.id}
                    />
                  </Box>
                )}

                {showAnswer && (
                  <Stack gap="md" align="center" style={{ width: '100%' }}>
                    <Text
                      size="xs"
                      fw={600}
                      tt="uppercase"
                      style={{
                        color: mutedColor,
                        letterSpacing: 1,
                        fontFamily: 'Inter, sans-serif',
                      }}
                    >
                      Respuesta
                    </Text>

                    <Text
                      size="lg"
                      ta="center"
                      fw={500}
                      style={{ color: textColor, fontFamily: 'Inter, sans-serif', lineHeight: 1.5 }}
                    >
                      {currentFlashcard?.respuesta}
                    </Text>

                    {currentFlashcard?.explicacion && (
                      <Box
                        style={{
                          width: '100%',
                          borderTop: `1px solid ${dark ? 'rgba(255,255,255,0.08)' : 'rgba(45,42,38,0.08)'}`,
                          paddingTop: 12,
                        }}
                      >
                        <Text
                          size="xs"
                          fw={600}
                          tt="uppercase"
                          ta="center"
                          mb={4}
                          style={{ color: mutedColor, letterSpacing: 1 }}
                        >
                          Explicación
                        </Text>
                        <Text size="sm" ta="center" style={{ color: mutedColor }}>
                          {currentFlashcard.explicacion}
                        </Text>
                      </Box>
                    )}
                  </Stack>
                )}
              </Card>
            </Box>
          </Box>

          {/* Acciones */}
          {showAnswer && (
            <Group justify="center" gap="md" wrap="wrap">
              <Button
                size="md"
                radius="md"
                variant="default"
                leftSection={<IconArrowRight size={16} />}
                onClick={handleNextFlashcard}
                styles={secondaryButtonStyles}
              >
                Siguiente sin marcar
              </Button>
              <Button
                size="md"
                radius="md"
                leftSection={<IconCheck size={16} />}
                onClick={handleMarkAsStudied}
                styles={primaryButtonStyles}
              >
                Marcar como estudiada
              </Button>
            </Group>
          )}
        </Stack>
      </Box>
    </Box>
  );
};

// Fondo de página consistente con el del Simulador
const pageBackground = (dark: boolean): React.CSSProperties => ({
  minHeight: '100vh',
  overflow: 'hidden',
  overflowY: 'auto',
  backgroundColor: dark ? 'rgba(15, 23, 42, 0.5)' : 'rgba(247, 243, 238, 0.3)',
});

export default EstudiarFlashcardsPage;
